import filter from 'lodash/filter';
import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import { ASSESSMENT_SESSION_ID } from '../base/constants';
import { ICyclesState, statePropName } from './slice';
import { CyclesError, ECycleStatus, ETrainingStatus, ICycleStatus, ISessionStatus, ITrainingDBStatus } from './types';
import {
  convertCycleStatusToApi,
  convertSessionStatusToApi,
  filterBySyncTimestamp,
  filterCFQWorkouts,
  filterNonSkippedCFQWorkouts,
  findFirstFinishedWorkout,
  findFirstUnfinishedSession,
  findFirstUnfinishedWorkout,
  findFirstUnfinishedWorkoutIndex,
  findRelativeActiveWorkoutIndex,
  findRelativeNonSkippedWorkoutIndex,
  getCycleSessionMapForWorkouts,
  isCFQWorkout,
  isSessionFinished,
  isSessionInactive,
  isWorkoutActive,
  isWorkoutFinished,
  removeCFQWorkouts,
  updateCycleSessionMapForCycles,
  updateCycleSessionMapForSessions,
} from './utils';

export interface IAppState {
  // this feature state
  [statePropName]: ICyclesState;
  // other features we don't care of
  [otherKeys: string]: any;
}

export const getCyclesState = (state: IAppState) => state[statePropName];

export const isUserPremium = (state: IAppState) => getCyclesState(state).isPremium;

export const isCyclesLoading = (state: IAppState) => getCyclesState(state).loading;

export const isCyclesSaving = (state: IAppState) => getCyclesState(state).saving;

export const getCurrentCycleId = (state: IAppState) => getCyclesState(state).currentCycleId;

export const getCurrentSessionId = (state: IAppState) => getCyclesState(state).currentSessionId;

export const getCycleLength = (state: IAppState) => getCyclesState(state).cycleLength;

export const getNumberOfFinishedSessions = (state: IAppState) => getCyclesState(state).finishedSessions;

const getNumberOfFinishedCycles = (state: IAppState) => getCyclesState(state).finishedCycles;

const getNumberOfCompletedWorkouts = (state: IAppState) => getCyclesState(state).numberWorkouts;

const getFirstTimestamp = (state: IAppState) => getCyclesState(state).firstTimestamp;
const getLastTimestamp = (state: IAppState) => getCyclesState(state).lastTimestamp;

export const getCurrentCycleStats = (state: IAppState) => {
  const currentCycleId = getCurrentCycleId(state);
  const currentCycleLength = getCycleLength(state);
  const currentSessionId = getCurrentSessionId(state);
  const finishedCycles = getNumberOfFinishedCycles(state);
  const finishedSessions = getNumberOfFinishedSessions(state);
  const numberWorkouts = getNumberOfCompletedWorkouts(state);
  const firstTimestamp = getFirstTimestamp(state);
  const lastTimestamp = getLastTimestamp(state);
  const stats: ITrainingDBStatus = {
    currentCycleId,
    currentCycleLength,
    currentSessionId,
    finishedCycles,
    finishedSessions,
    numberWorkouts,
    firstTimestamp,
    lastTimestamp,
  };
  return stats;
};

export const isOnboardingFinished = (state: IAppState) => {
  // if cycle id is greater than 1 -> onboarding was finished
  const currentCycleId = getCurrentCycleId(state);
  if (currentCycleId > 1) {
    return true;
  }
  // if current session id is greater than assessment id -> onboarding was finished
  const currentSessionId = getCurrentSessionId(state);
  if (currentSessionId > ASSESSMENT_SESSION_ID) {
    return true;
  }
  // if cycle was populated with sessions -> onboarding was finished
  // if cycle is empty we need to go to onboarding and finish the process
  return getCycleLength(state) > 1;
};

export const getCycleStatus = (state: IAppState) => getCyclesState(state).cycleStatus;

export const isCycleCompleted = (state: IAppState) => getCycleStatus(state) === ECycleStatus.CycleCompleted;

export const isAssessmentRunning = (state: IAppState) => {
  const cycleState = getCycleStatus(state);
  return cycleState === ECycleStatus.AssessmentRunning || cycleState === ECycleStatus.AssessmentFinished;
};

export const isAssessmentFinished = (state: IAppState) => getCycleStatus(state) !== ECycleStatus.AssessmentRunning;

export const isSessionRunningStatus = (state: IAppState) => getCycleStatus(state) === ECycleStatus.SessionRunning;

export const getCycleStartTimestamp = (state: IAppState) => getCyclesState(state).startTimestamp;

export const getCycleFinishedTimestamp = (state: IAppState) => getCyclesState(state).finishedTimestamp;

const getAllSessions = (state: IAppState) => getCyclesState(state).sessions;

export const getCycleSessions = (state: IAppState, cycleId: number) =>
  sortBy(filter(getAllSessions(state), { cycleId }), 'sessionId');

export const getCycleSession = (state: IAppState, cycleId: number, sessionId: number) =>
  find(getAllSessions(state), { cycleId, sessionId });

export const isSessionOngoing = (state: IAppState, cycleId: number, sessionId: number) => {
  const session = getCycleSession(state, cycleId, sessionId);
  if (!session) {
    return false;
  }
  if (isSessionInactive(session)) {
    return false;
  }
  if (session.status === ETrainingStatus.Ongoing) {
    return true;
  }
  const workouts = getSessionWorkouts(state, cycleId, sessionId);
  const finishedWorkout = findFirstUnfinishedWorkoutIndex(workouts);
  return !!finishedWorkout;
};

export const getCurrentSession = (state: IAppState) => {
  const cycleId = getCurrentCycleId(state);
  const sessionId = getCurrentSessionId(state);
  return getCycleSession(state, cycleId, sessionId);
};

export const isCurrentSessionFinished = (state: IAppState) => {
  const session = getCurrentSession(state);
  return isSessionFinished(session) || isSessionInactive(session);
};

export const isCurrentCycleSessionFinished = (state: IAppState, sessionId: number) => {
  const cycleId = getCurrentCycleId(state);
  const session = getCycleSession(state, cycleId, sessionId);
  return isSessionFinished(session) || isSessionInactive(session);
};

export const getCurrentSessionDomain = (state: IAppState) => {
  const session = getCurrentSession(state);
  return session ? session.domainId : 0;
};

const getAllWorkouts = (state: IAppState) => getCyclesState(state).workouts;

export const getSessionWorkouts = (state: IAppState, cycleId: number, sessionId: number) =>
  sortBy(filter(getAllWorkouts(state), { cycleId, sessionId }), 'index');

const getAllCycles = (state: IAppState) => getCyclesState(state).cycles;

export const getCycle = (state: IAppState, cycleId: number) => find(getAllCycles(state), { cycleId });

export const getCurrentSessionWorkouts = (state: IAppState) => {
  const cycleId = getCurrentCycleId(state);
  const sessionId = getCurrentSessionId(state);
  return getSessionWorkouts(state, cycleId, sessionId);
};

export const getCurrentSessionWorkoutsWithoutCFQ = (state: IAppState) => {
  const workouts = getCurrentSessionWorkouts(state);
  return removeCFQWorkouts(workouts);
};

export const getSessionWorkoutByIndex = (state: IAppState, cycleId: number, sessionId: number, index: number) => {
  const workouts = getSessionWorkouts(state, cycleId, sessionId);
  return find(workouts, { index });
};

export const getCurrentSessionWorkoutByRelativeIndex = (state: IAppState, index: number) => {
  const workouts = getCurrentSessionWorkouts(state);
  return workouts[index];
};

export const isCurrentSessionWorkoutByRelativeIndexFinished = (state: IAppState, index: number) => {
  const workout = getCurrentSessionWorkoutByRelativeIndex(state, index);
  if (!workout) {
    throw new CyclesError(`Workout index ${index} not found in current session!`);
  }
  return !isWorkoutActive(workout);
};

export const doesCurrentSessionHasCFQWorkouts = (state: IAppState) => {
  const workouts = getCurrentSessionWorkouts(state);
  const cfqWorkouts = filterCFQWorkouts(workouts);
  return cfqWorkouts.length > 0;
};

export const getCurrentSessionFirstActiveWorkout = (state: IAppState) => {
  const workouts = getCurrentSessionWorkouts(state);
  return findFirstUnfinishedWorkout(workouts);
};

export const getCurrentSessionFirstActiveWorkoutId = (state: IAppState) => {
  const activeWorkout = getCurrentSessionFirstActiveWorkout(state);
  return activeWorkout?.workoutId;
};

export const cfqWorkoutsStartedInCurrentSession = (state: IAppState) => {
  const workouts = getCurrentSessionWorkouts(state);
  const cfqWorkouts = filterNonSkippedCFQWorkouts(workouts);
  if (cfqWorkouts.length === 0) {
    return false;
  }
  // check if we have at least one finished or skipped workout
  const finishedWorkout = findFirstFinishedWorkout(cfqWorkouts);
  return !!finishedWorkout;
};

export const cfqWorkoutToBeStartedInCurrentSession = (state: IAppState) => {
  const workouts = getCurrentSessionWorkouts(state);
  const cfqWorkouts = filterNonSkippedCFQWorkouts(workouts);
  if (cfqWorkouts.length === 0) {
    return false;
  }
  const activeWorkout = getCurrentSessionFirstActiveWorkout(state);
  if (!activeWorkout) {
    return false;
  }
  return isCFQWorkout(activeWorkout);
};

/**
 * returns relative index of the given workout in the current session
 * note: this will work properly only for assessment/CFQ since
 * in normal session we might have same workoutId's
 * Note: in assessment we now also can have same workouts
 * in case they are skipped due to invalidation
 * so second selector is implemented as workaround
 * we might think about how to fix it in future.
 * first selector selects indices for CFQ (includes completed workouts)
 * which are needed for progress UI
 * second used to find proper workout index in the session to save cycle data
 */
export const getNonSkippedWorkoutRelativeIndex = (state: IAppState, workoutId: number) => {
  const workouts = getCurrentSessionWorkouts(state);
  return findRelativeNonSkippedWorkoutIndex(workouts, workoutId);
};

export const getActiveWorkoutRelativeIndex = (state: IAppState, workoutId: number) => {
  const workouts = getCurrentSessionWorkouts(state);
  return findRelativeActiveWorkoutIndex(workouts, workoutId);
};

export const getCFQSessionLength = (state: IAppState) => {
  const workouts = getCurrentSessionWorkouts(state);
  const cfqSession = filterNonSkippedCFQWorkouts(workouts);
  return cfqSession.length;
};

export const getCFQWorkoutRelativeIndex = (state: IAppState, workoutId: number) => {
  const workouts = getCurrentSessionWorkouts(state);
  const cfqSession = filterNonSkippedCFQWorkouts(workouts);
  return findRelativeNonSkippedWorkoutIndex(cfqSession, workoutId);
};

export const getUnsyncedWorkouts = (state: IAppState) => filterBySyncTimestamp(getAllWorkouts(state));

export const getUnsyncedSessions = (state: IAppState) => filterBySyncTimestamp(getAllSessions(state));

export const getUnsyncedCycles = (state: IAppState) => filterBySyncTimestamp(getAllCycles(state));

export const getCyclesToSync = (state: IAppState) => {
  // collect information about all unsynced sessions and cycles
  const workoutsToSync = getUnsyncedWorkouts(state);
  const sessionsToSync = getUnsyncedSessions(state);
  const cyclesToSync = getUnsyncedCycles(state);
  let cycleMap = getCycleSessionMapForWorkouts(workoutsToSync);
  cycleMap = updateCycleSessionMapForSessions(cycleMap, sessionsToSync);
  cycleMap = updateCycleSessionMapForCycles(cycleMap, cyclesToSync);
  // start to convert collected data to actual payload
  const toSave: ICycleStatus[] = [];
  for (const [cycleId, sessions] of Array.from(cycleMap)) {
    const sessionsToSave: ISessionStatus[] = [];
    // convert all sessions to api format
    // api supports empty sessions property
    // so we don't need to care about this edge case
    for (const sessionId of sessions) {
      const workouts = getSessionWorkouts(state, cycleId, sessionId);
      const session = getCycleSession(state, cycleId, sessionId);
      if (!session) {
        continue;
      }
      const sessionToSync = convertSessionStatusToApi(session, workouts);
      sessionsToSave.push(sessionToSync);
    }
    const cycle = getCycle(state, cycleId);
    if (!cycle) {
      continue;
    }
    const cycleToSync = convertCycleStatusToApi(cycle, sessionsToSave);
    toSave.push(cycleToSync);
  }
  return toSave;
};

export const getAssessmentWorkoutIds = (state: IAppState, cycleId: number) => {
  const session = getCycleSession(state, cycleId, ASSESSMENT_SESSION_ID);
  if (!session) {
    return;
  }
  const workouts = getSessionWorkouts(state, cycleId, ASSESSMENT_SESSION_ID);
  return workouts.map((w) => w.workoutId);
};

export const areWorkoutIdAndIndexCorrect = (state: IAppState, workoutId: number, workoutIndex?: number) => {
  const workouts = getCurrentSessionWorkouts(state);
  if (!workoutIndex) {
    // if index not selected we always can replace workout
    // and we don't need to check indices
    return true;
  }
  // index is relative array index in this case
  const pointedWorkoutId = workouts[workoutIndex]?.workoutId;
  // if workout ids are same then workout is really at this index
  // and we can replace current workout with it
  return pointedWorkoutId === workoutId;
};

export const getCurrentCycleSessions = (state: IAppState) => {
  const cycleId = getCurrentCycleId(state);
  return getCycleSessions(state, cycleId);
};

export const getAmountOfSessionsInCurrentCycle = (state: IAppState) => getCurrentCycleSessions(state).length;

export const getSessionLength = (state: IAppState, cycleId: number, sessionId: number) =>
  getSessionWorkouts(state, cycleId, sessionId).length;

export const getCurrentSessionLength = (state: IAppState) => {
  const cycleId = getCurrentCycleId(state);
  const sessionId = getCurrentSessionId(state);
  return getSessionLength(state, cycleId, sessionId);
};

export const getNextSessionId = (state: IAppState) => {
  const cycleId = getCurrentCycleId(state);
  const session = findFirstUnfinishedSession(getCycleSessions(state, cycleId));
  return session ? session.sessionId : ASSESSMENT_SESSION_ID;
};

export const getSessionsLeftCount = (state: IAppState) => getCycleLength(state) - getCurrentSessionId(state) + 1;

export const getCurrentSessionLengthWithoutCFQ = (state: IAppState) => {
  const workouts = getCurrentSessionWorkoutsWithoutCFQ(state);
  return workouts.length;
};

export const getAssessmentFinishTimestamp = (state: IAppState, cycleId: number) => {
  const session = getCycleSession(state, cycleId, ASSESSMENT_SESSION_ID);
  if (!session) {
    return 0;
  }
  return session.finishedTimestamp;
};

export const wasAssessmentFullyCompleted = (state: IAppState, cycleId: number, supportedWorkoutIds: number[]) => {
  const session = getCycleSession(state, cycleId, ASSESSMENT_SESSION_ID);
  if (!session || !isSessionFinished(session)) {
    return false;
  }
  const workouts = getSessionWorkouts(state, cycleId, ASSESSMENT_SESSION_ID);
  return workouts.reduce((completed, w) => {
    const supported = supportedWorkoutIds.includes(w.workoutId);
    // check finished status only for supported workouts
    return completed && (supported ? isWorkoutFinished(w) : true);
  }, true);
};
